import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDocs, query, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuCircleX, LuClock, LuEye, LuInbox, LuLoaderCircle, LuMessageSquareReply, LuSend, LuUser } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { TextArea } from '../../../components/form/FormControls';
import { auth, db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { DetailItem, EmptyState, LoadingRows, SearchField, SectionHeader } from '../components/ProfileUi';
import ComplaintCard, { ComplaintReplies, ComplaintStatusBadge } from './ComplaintCard';
import { COMPLAINT_STATUSES, complaintStatusKey, complaintTypeLabel, formatLongDate, toDate } from './complaintOptions';

const STATUS_ICONS = { PENDING: LuClock, INCELENIYOR: LuEye, RESOLVED: LuCircleCheck, CLOSED: LuCircleX };

/**
 * "Gelen Dilekçeler": admins see every petition, the people in charge the
 * ones sent to them. A petition opens to change its status and reply.
 */
const IncomingComplaints = ({ isAdmin, isComplaintTarget, userData }) => {
  const { t, language, locale } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const label = (key, vars) => t(`profilePage.complaints.incoming.${key}`, vars);
  const [allComplaints, setAllComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchAllComplaints = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);
    try {
      const querySnapshot = await getDocs(query(collection(db, 'Complaints')));
      let complaints = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Admin hepsini, muhataplar yalnızca kendilerine gönderilenleri görür
      if (!isAdmin && isComplaintTarget) {
        complaints = complaints.filter((complaint) => complaint.targetEmails?.some((email) => email.toLowerCase() === user.email.toLowerCase()));
      }
      complaints.sort((a, b) => toDate(b.createdAt) - toDate(a.createdAt));
      setAllComplaints(complaints);
    } catch (error) {
      console.error('Error fetching all complaints: ', error);
      toast.error(tRef.current('profilePage.complaints.toast.incomingError'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isComplaintTarget]);

  useEffect(() => {
    if (isAdmin || isComplaintTarget) fetchAllComplaints();
  }, [isAdmin, isComplaintTarget, fetchAllComplaints]);

  const handleComplaintStatusUpdate = async (newStatus) => {
    if (!selectedComplaint) return;
    setStatusLoading(true);
    try {
      await updateDoc(doc(db, 'Complaints', selectedComplaint.id), { status: newStatus, updatedAt: new Date() });
      setAllComplaints((prev) => prev.map((c) => (c.id === selectedComplaint.id ? { ...c, status: newStatus } : c)));
      toast.success(label('statusUpdated'));
      setSelectedComplaint(null);
    } catch (error) {
      console.error('Error updating complaint status: ', error);
      toast.error(label('statusError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!selectedComplaint || !replyText.trim()) {
      toast.error(label('replyRequired'));
      return;
    }
    setReplyLoading(true);
    try {
      const user = auth.currentUser;
      const replierName = userData.eName && userData.eSurname
        ? `${userData.eName} ${userData.eSurname}`
        : (user?.displayName || user?.email || 'Yetkili');
      const newReply = {
        text: replyText.trim(),
        repliedBy: replierName,
        repliedByEmail: user?.email || '',
        repliedAt: new Date(),
      };
      const updatedReplies = [...(selectedComplaint.replies || []), newReply];
      await updateDoc(doc(db, 'Complaints', selectedComplaint.id), { replies: updatedReplies, lastReplyAt: new Date() });
      setAllComplaints((prev) => prev.map((c) => (c.id === selectedComplaint.id ? { ...c, replies: updatedReplies, lastReplyAt: new Date() } : c)));
      setSelectedComplaint((prev) => ({ ...prev, replies: updatedReplies, lastReplyAt: new Date() }));
      toast.success(label('replySent'));
      setReplyText('');
    } catch (error) {
      console.error('Error submitting reply: ', error);
      toast.error(label('replyError'));
    } finally {
      setReplyLoading(false);
    }
  };

  const counts = useMemo(() => {
    const result = { all: allComplaints.length };
    COMPLAINT_STATUSES.forEach((status) => {
      result[status] = allComplaints.filter((c) => complaintStatusKey(c.status) === status).length;
    });
    return result;
  }, [allComplaints]);

  const term = search.trim().toLocaleLowerCase('tr');
  const shown = allComplaints
    .filter((complaint) => filter === 'all' || complaintStatusKey(complaint.status) === filter)
    .filter((complaint) => !term || [complaint.subject, complaint.employeeName, complaint.employeeEmail, complaint.documentNumber]
      .some((value) => String(value || '').toLocaleLowerCase('tr').includes(term)));

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuInbox}
        gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30"
        title={label('title')}
        description={isAdmin ? label('descriptionAdmin') : label('descriptionTarget')}
      />

      {!loading && allComplaints.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-2 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-overflow-x-auto tw-pb-1">
            <SegmentedControl
              size="sm"
              layoutId="profile-incoming-filter"
              label={label('filterLabel')}
              value={filter}
              onChange={setFilter}
              options={['all', ...COMPLAINT_STATUSES].map((key) => ({
                value: key,
                label: `${key === 'all' ? label('all') : t(`profilePage.complaints.status.${key}`)} (${counts[key]})`,
              }))}
            />
          </div>
          <SearchField value={search} onChange={setSearch} placeholder={label('search')} className="lg:tw-w-72" />
        </div>
      )}

      {loading && <LoadingRows count={3} height="tw-h-28" />}
      {!loading && allComplaints.length === 0 && <EmptyState icon={LuInbox} title={label('empty')} />}
      {!loading && allComplaints.length > 0 && shown.length === 0 && <EmptyState compact icon={LuInbox} title={label('noMatch')} />}
      {!loading && shown.length > 0 && (
        <ul className="tw-space-y-3">
          {shown.map((complaint) => (
            <ComplaintCard
              key={complaint.id}
              complaint={complaint}
              onOpen={(item) => {
                setSelectedComplaint(item);
                setReplyText('');
              }}
            />
          ))}
        </ul>
      )}

      <Modal
        isOpen={Boolean(selectedComplaint)}
        onClose={() => !statusLoading && !replyLoading && setSelectedComplaint(null)}
        size="lg"
        title={selectedComplaint?.subject || label('detailTitle')}
        subtitle={selectedComplaint?.documentNumber || label('detailTitle')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
            <LuInbox aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={<Button variant="ghost" onClick={() => setSelectedComplaint(null)}>{t('common.close')}</Button>}
      >
        {selectedComplaint && (
          <div className="tw-space-y-5">
            <dl className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <DetailItem label={label('sender')}>
                <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuUser aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />{selectedComplaint.employeeName}</span>
                <span className="tw-block tw-text-xs tw-font-normal tw-text-slate-500 dark:tw-text-slate-400">{selectedComplaint.employeeEmail}</span>
              </DetailItem>
              <DetailItem label={label('date')}>{formatLongDate(selectedComplaint.createdAt, locale)}</DetailItem>
              <DetailItem label={label('type')}><Badge tone="accent">{complaintTypeLabel(t, selectedComplaint)}</Badge></DetailItem>
              <DetailItem label={label('currentStatus')}><ComplaintStatusBadge status={selectedComplaint.status} /></DetailItem>
              {selectedComplaint.targetNames?.length > 0 && <DetailItem label={label('targets')} wide>{selectedComplaint.targetNames.join(', ')}</DetailItem>}
            </dl>

            <div className="tw-space-y-1.5">
              <p className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('details')}</p>
              <p className="tw-whitespace-pre-line tw-break-words tw-rounded-2xl tw-bg-slate-500/[0.06] tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed tw-text-slate-800 dark:tw-text-slate-100">{selectedComplaint.details}</p>
            </div>

            <div className="tw-space-y-2">
              <p className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('changeStatus')}</p>
              <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
                {COMPLAINT_STATUSES.map((status) => {
                  const Icon = STATUS_ICONS[status];
                  const isActive = selectedComplaint.status === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleComplaintStatusUpdate(status)}
                      disabled={statusLoading || isActive}
                      aria-pressed={isActive}
                      className={`tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-2xl tw-border tw-px-2 tw-py-3 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed ${
                        isActive
                          ? 'tw-border-teal-500/50 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-text-teal-200'
                          : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-700 hover:tw-bg-white disabled:tw-opacity-60 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.06]'
                      }`}
                    >
                      {statusLoading && !isActive ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />}
                      {t(`profilePage.complaints.status.${status}`)}
                      {isActive && <span className="tw-text-[10px] tw-font-medium tw-opacity-70">{label('current')}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <ComplaintReplies replies={selectedComplaint.replies} />

            <div className="tw-space-y-2">
              <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
                <LuMessageSquareReply aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {label('writeReply')}
              </p>
              <TextArea rows={4} value={replyText} onChange={(event) => setReplyText(event.target.value)} placeholder={label('replyPlaceholder')} aria-label={label('writeReply')} className="tw-resize-y" />
              <div className="tw-flex tw-justify-end">
                <Button variant="primary" size="sm" onClick={handleReplySubmit} disabled={replyLoading || !replyText.trim()}>
                  {replyLoading ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
                  {replyLoading ? label('sending') : label('sendReply')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

IncomingComplaints.propTypes = {
  isAdmin: PropTypes.bool,
  isComplaintTarget: PropTypes.bool,
  userData: PropTypes.object.isRequired,
};

export default IncomingComplaints;
